import { API_CALL } from 'actions/actionConstants'
import type { DefinedAppAction } from 'actions/types/ActionTypes'
import { FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS, FETCH_LOYALTY_ACCOUNT } from 'actions/apiActionConstants'
import requestLoyaltyAccount from 'api/loyalty/requestLoyaltyAccount'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import moment from 'moment'

import type { ApiActionResult } from 'middlewares/apiMiddleware'
import type { ThunkAction } from 'redux-thunk'
import type { AppDispatch } from 'src/client/store'
import wait from 'lib/promise/wait'

const LOYALTY_ACCOUNT_FETCH_POLL_MS = 50
const LOYALTY_ACCOUNT_FETCH_MAX_WAIT_MS = 5000
const LOYALTY_ACCOUNT_TTL_IN_SECONDS = 30

interface FetchLoyaltyAccountOptions {
  force?: boolean
}

function awaitLoyaltyAccountFetch(dispatch: AppDispatch, token?: string): Promise<boolean> {
  return new Promise(resolve => {
    dispatch({
      type: API_CALL,
      api: FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS,
      request: () => requestLoyaltyAccount(token),
      onComplete: (result: ApiActionResult<Awaited<ReturnType<typeof requestLoyaltyAccount>>>) => resolve(result.status === 'success'),
    })
  })
}

function fetchLoyaltyAccount(token?: string, options: FetchLoyaltyAccountOptions = {}): ThunkAction<Promise<boolean | void>, App.State, undefined, DefinedAppAction> {
  const { force = false } = options
  return async(dispatch, getState) => {
    const state = getState()

    if (!getIsLoyaltyEnabled(state)) {
      return
    }

    const hasLoyaltyAccount = getHasLoyaltyAccount(state)
    const loyaltyAccount = state.loyalty.account

    if (force) {
      if (loyaltyAccount.fetching) {
        const deadline = Date.now() + LOYALTY_ACCOUNT_FETCH_MAX_WAIT_MS
        while (getState().loyalty.account.fetching && Date.now() < deadline) {
          await wait(LOYALTY_ACCOUNT_FETCH_POLL_MS)
        }
        if (getHasLoyaltyAccount(getState())) return true
      }
      return await awaitLoyaltyAccountFetch(dispatch, token)
    }

    if (
      loyaltyAccount.data?.fetchedAt &&
      moment().utc().diff(moment(loyaltyAccount.data.fetchedAt).utc(), 'seconds') >= LOYALTY_ACCOUNT_TTL_IN_SECONDS
    ) {
      dispatch({
        type: API_CALL,
        api: FETCH_LATEST_LOYALTY_ACCOUNT_DETAILS,
        request: () => requestLoyaltyAccount(),
      })
    }

    if (
      hasLoyaltyAccount ||
      loyaltyAccount.fetching ||
      loyaltyAccount.error
    ) {
      return
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LOYALTY_ACCOUNT,
      request: () => requestLoyaltyAccount(token),
    })
  }
}

export default fetchLoyaltyAccount
